import React, { useEffect, useRef, memo, useMemo } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import type { GeoJSON } from 'geojson'
import { MapboxMapCore } from './MapboxMapCore'
import { useRouteLayer } from '../../hooks/useRouteLayer'
import { useWaypointLayer } from '../../hooks/useWaypointLayer'
import { usePOILayer } from '../../hooks/usePOILayer'
import { usePOIMarkers } from '../../hooks/usePOIMarkers'
import { useDeadEndLayer } from '../../hooks/useDeadEndLayer'
import { useCampsiteDiscoveryMarkers } from '../../hooks/useCampsiteDiscoveryMarkers'
import { useRouteBufferLayer } from '../../hooks/useRouteBufferLayer'
import { useMapClickMarker } from '../../hooks/useMapClickMarker'
import { useSearchResultMarker } from '../../hooks/useSearchResultMarker'
import { useSelectedWaypointMarker } from '../../hooks/useSelectedWaypointMarker'
import { useSearchBboxLayer, type SearchBboxVisualization } from '../../hooks/useSearchBboxLayer'
import { useMapCentering } from '../../hooks/useMapCentering'
import { useDrawingLayers } from '../../hooks/useDrawingLayers'
import { useDrawingMode } from '../../hooks/useDrawingMode'
import { useTimelineRoutePreview } from '../../hooks/useTimelineRoutePreview'
import { useMapboxMapContext } from '../../contexts/MapboxMapContext'
import { useForestRoadLayer } from '../../hooks/useForestRoadLayer'
import { usePublicLandsLayer } from '../../hooks/usePublicLandsLayer'
import { useDispersedCampsiteLayer } from '../../hooks/useDispersedCampsiteLayer'
import { useRouteStartEndMarkers } from '../../hooks/useRouteStartEndMarkers'
import { useItineraryWaypointMarkers } from '../../hooks/useItineraryWaypointMarkers'
import { useItineraryBuilderWaypointMarkers } from '@/pages/itinerary-builder/hooks/useItineraryBuilderWaypointMarkers'
import { useGpxImportedWaypointMarkers } from '../../hooks/useGpxImportedWaypointMarkers'
import { useSubscription } from '@/modules/subscriptions/hooks/useSubscription'

export interface MapViewport {
  zoom: number
  bounds: {
    north: number
    south: number
    east: number
    west: number
  }
  center: {
    latitude: number
    longitude: number
  }
}

interface MapboxMapProps {
  routes?: any[]
  waypoints?: any[]
  poiResults?: any[] // POI search results to display as temporary markers
  searchResult?: { id: string; name: string; latitude: number; longitude: number; category?: string; poi_type?: string; address?: string } | null // Search result to display as marker
  selectedRoute?: any
  selectedPOI?: any // Currently selected POI
  selectedWaypoint?: any // Currently selected waypoint
  onRouteClick?: (route: any) => void
  onWaypointClick?: (waypoint: any) => void
  onPOIClick?: (poi: any) => void // Callback when POI marker is clicked
  onDeadEndClick?: (deadEnd: any) => void // Callback when dead end marker is clicked
  deadEnds?: any[] // Dead end markers for campsite discovery
  selectedDeadEnd?: any // Currently selected dead end
  searchRouteGeometry?: GeoJSON.LineString | GeoJSON.MultiLineString | null // Route geometry for search radius visualization
  searchRadiusMiles?: number | null // Search radius in miles for buffer visualization
  searchBboxVisualization?: SearchBboxVisualization | null // Bbox visualization data from API response
  onMapClick?: (coords: any) => void
  height?: string
  className?: string
  // Drawing mode props
  isDrawMode?: boolean
  drawingPoints?: [number, number][]
  snappedGeometry?: GeoJSON.LineString | null
  currentHoverPoint?: [number, number] | null
  hoverPreviewGeometry?: GeoJSON.LineString | null
  isSnapping?: boolean
  isHoverSnapping?: boolean
  onDrawingPointAdd?: (coords: [number, number]) => void
  onDrawingHover?: (coords: [number, number] | null) => void
  // Map centering
  centerOnRoute?: any
  centerOnWaypoint?: any
  centerOnWaypoints?: Array<{ latitude?: number | null; longitude?: number | null }> | null
  centerOnSearch?: { latitude: number; longitude: number } | null
  centerOnUserLocation?: { latitude: number; longitude: number } | null
  centerOnCandidate?: { latitude: number; longitude: number } | null
  centerOnPOI?: { latitude: number; longitude: number } | null
  centerOnDeadEnd?: { latitude: number; longitude: number } | null
  // Map click popup
  clickedMapPoint?: { latitude: number; longitude: number } | null
  onCloseMapClickPopup?: () => void
  onSaveMapClickPoint?: () => void
  // Map click marker
  mapClickCoordinates?: { latitude: number; longitude: number } | null
  // Forest road discovery mode
  isDeadEndDiscoveryMode?: boolean
  getAccessToken?: () => Promise<string | null>
  onForestRoadsError?: (message: string | null) => void
  onViewportChange?: (viewport: MapViewport) => void
  tripRouteSchedule?: any[] // TripRouteScheduleRoute[] for start/end markers
  itineraryRouteForWaypoint?: string | null // Route ID for which itinerary waypoints should be shown as numbered badges
  selectedTripId?: string | null // Trip ID - only show numbered badges when viewing an itinerary
  trip?: any | null // Trip data for itinerary builder day-based markers
  isItineraryBuilder?: boolean // Whether this is the itinerary builder (uses day-based markers)
  showLibraryWaypoints?: boolean // Toggle to show/hide library waypoints on map
  showPublicLands?: boolean // Toggle to show/hide public land boundaries
  onPublicLandsLoadingChange?: (isLoading: boolean) => void // Callback for loading state changes
  showDispersedCampsites?: boolean // Toggle to show/hide dispersed camp finder layer
  onDispersedCampsitesLoadingChange?: (isLoading: boolean) => void // Callback for camp finder loading state
  onDispersedCampsitesUpgradeRequired?: () => void // Called when the campsite API returns 403 (Pro required)
  /** If false, scroll/wheel over the map will not zoom (page scrolls instead). Default true. */
  scrollZoom?: boolean
}

export function MapboxMap({
  routes = [],
  waypoints = [],
  poiResults = [],
  searchResult = null,
  selectedRoute,
  selectedPOI,
  selectedWaypoint,
  onRouteClick,
  onWaypointClick,
  onPOIClick,
  onDeadEndClick,
  deadEnds = [],
  selectedDeadEnd,
  searchRouteGeometry = null,
  searchRadiusMiles = null,
  searchBboxVisualization = null,
  onMapClick,
  height = '400px',
  className = '',
  isDrawMode = false,
  drawingPoints = [],
  snappedGeometry = null,
  currentHoverPoint = null,
  hoverPreviewGeometry = null,
  isSnapping = false,
  isHoverSnapping = false,
  onDrawingPointAdd,
  onDrawingHover,
  centerOnRoute,
  centerOnWaypoint,
  centerOnWaypoints,
  centerOnSearch,
  centerOnUserLocation,
  centerOnCandidate,
  centerOnPOI,
  centerOnDeadEnd,
  clickedMapPoint,
  onCloseMapClickPopup,
  onSaveMapClickPoint,
  mapClickCoordinates,
  isDeadEndDiscoveryMode = false,
  getAccessToken,
  onForestRoadsError,
  onViewportChange,
  tripRouteSchedule,
  itineraryRouteForWaypoint,
  selectedTripId,
  trip,
  isItineraryBuilder = false,
  showLibraryWaypoints = false,
  showPublicLands = false,
  onPublicLandsLoadingChange,
  showDispersedCampsites = false,
  onDispersedCampsitesLoadingChange,
  onDispersedCampsitesUpgradeRequired,
  scrollZoom
}: MapboxMapProps) {
  const popupRef = useRef<mapboxgl.Popup | null>(null)
  const { isFree, loading: subscriptionLoading, status } = useSubscription()

  // Default to free tier style when loading or unknown (safer default)
  // Only use pro style if we're certain the user is pro/team
  const mapStyleUrl = useMemo(() => {
    // If subscription is still loading or status is null, default to free tier
    if (subscriptionLoading || !status) {
      return 'mapbox://styles/ayott/cmd5j61z0035p01r471h07na0'
    }
    // Once we know the tier, use appropriate style
    return isFree
      ? 'mapbox://styles/ayott/cmd5j61z0035p01r471h07na0' // Free-tier style
      : 'mapbox://styles/ayott/cmdldmyxv00eu01ptcd7acb7k' // Pro/Team style
  }, [isFree, subscriptionLoading, status])

  // Use MapboxMapCore for map initialization and click handlers
  return (
    <MapboxMapCore
      styleUrl={mapStyleUrl}
      onRouteClick={onRouteClick}
      onWaypointClick={onWaypointClick}
      onPOIClick={onPOIClick}
      onMapClick={onMapClick}
      height={height}
      className={className}
      scrollZoom={scrollZoom}
    >
      {/* Layer hooks - these use the map context from MapboxMapCore */}
      <MapViewportListener onViewportChange={onViewportChange} />
      <MapboxMapLayers
        routes={routes}
        waypoints={waypoints}
        poiResults={poiResults}
        searchResult={searchResult}
        selectedRoute={selectedRoute}
        selectedPOI={selectedPOI}
        selectedWaypoint={selectedWaypoint}
        onWaypointClick={onWaypointClick}
        onPOIClick={onPOIClick}
        deadEnds={deadEnds}
        selectedDeadEnd={selectedDeadEnd}
        onDeadEndClick={onDeadEndClick}
        searchRouteGeometry={searchRouteGeometry}
        searchRadiusMiles={searchRadiusMiles}
        searchBboxVisualization={searchBboxVisualization}
        mapClickCoordinates={mapClickCoordinates}
        centerOnWaypoint={centerOnWaypoint}
        centerOnWaypoints={centerOnWaypoints}
        centerOnRoute={centerOnRoute}
        centerOnSearch={centerOnSearch}
        centerOnUserLocation={centerOnUserLocation}
        centerOnCandidate={centerOnCandidate}
        centerOnPOI={centerOnPOI}
        centerOnDeadEnd={centerOnDeadEnd}
        isDrawMode={isDrawMode}
        drawingPoints={drawingPoints}
        snappedGeometry={snappedGeometry}
        currentHoverPoint={currentHoverPoint}
        hoverPreviewGeometry={hoverPreviewGeometry}
        isHoverSnapping={isHoverSnapping}
        onDrawingPointAdd={onDrawingPointAdd}
        onDrawingHover={onDrawingHover}
        isDeadEndDiscoveryMode={isDeadEndDiscoveryMode}
        getAccessToken={getAccessToken}
        onForestRoadsError={onForestRoadsError}
        tripRouteSchedule={tripRouteSchedule}
        itineraryRouteForWaypoint={itineraryRouteForWaypoint}
        selectedTripId={selectedTripId}
        trip={trip}
        isItineraryBuilder={isItineraryBuilder}
        showLibraryWaypoints={showLibraryWaypoints}
        showPublicLands={showPublicLands}
        onPublicLandsLoadingChange={onPublicLandsLoadingChange}
        showDispersedCampsites={showDispersedCampsites}
        onDispersedCampsitesLoadingChange={onDispersedCampsitesLoadingChange}
        onDispersedCampsitesUpgradeRequired={onDispersedCampsitesUpgradeRequired}
      />
      
      {/* Popup for clicked map point - must be inside provider to access context */}
      <MapClickPopup
        clickedMapPoint={clickedMapPoint}
        onCloseMapClickPopup={onCloseMapClickPopup}
        onSaveMapClickPoint={onSaveMapClickPoint}
        popupRef={popupRef}
      />
    </MapboxMapCore>
  )
}

/**
 * Internal component that uses all the layer hooks
 * Must be inside MapboxMapCore to access map context
 */
function MapboxMapLayers({
  routes,
  waypoints,
  poiResults,
  searchResult,
  selectedRoute,
  selectedPOI,
  selectedWaypoint,
  onWaypointClick,
  onPOIClick,
  deadEnds,
  selectedDeadEnd,
  onDeadEndClick,
  searchRouteGeometry,
  searchRadiusMiles,
  searchBboxVisualization,
  mapClickCoordinates,
  centerOnWaypoint,
  centerOnWaypoints,
  centerOnRoute,
  centerOnSearch,
  centerOnUserLocation,
  centerOnCandidate,
  centerOnPOI,
  centerOnDeadEnd,
  isDrawMode,
  drawingPoints,
  snappedGeometry,
  currentHoverPoint,
  hoverPreviewGeometry,
  isHoverSnapping,
  onDrawingPointAdd,
  onDrawingHover,
  isDeadEndDiscoveryMode,
  getAccessToken,
  onForestRoadsError,
  tripRouteSchedule,
  itineraryRouteForWaypoint,
  selectedTripId,
  trip,
  isItineraryBuilder,
  showLibraryWaypoints,
  showPublicLands,
  onPublicLandsLoadingChange,
  showDispersedCampsites,
  onDispersedCampsitesLoadingChange,
  onDispersedCampsitesUpgradeRequired
}: {
  routes: any[]
  waypoints: any[]
  poiResults: any[]
  searchResult: any
  selectedRoute?: any
  selectedPOI?: any
  selectedWaypoint?: any
  onWaypointClick?: (waypoint: any) => void
  onPOIClick?: (poi: any) => void
  deadEnds?: any[]
  selectedDeadEnd?: any
  onDeadEndClick?: (deadEnd: any) => void
  searchRouteGeometry?: GeoJSON.LineString | GeoJSON.MultiLineString | null
  searchRadiusMiles?: number | null
  searchBboxVisualization?: SearchBboxVisualization | null
  mapClickCoordinates?: { latitude: number; longitude: number } | null
  centerOnWaypoint?: any
  centerOnWaypoints?: Array<{ latitude?: number | null; longitude?: number | null }> | null
  centerOnRoute?: any
  centerOnSearch?: { latitude: number; longitude: number } | null
  centerOnUserLocation?: { latitude: number; longitude: number } | null
  centerOnCandidate?: { latitude: number; longitude: number } | null
  centerOnPOI?: { latitude: number; longitude: number } | null
  centerOnDeadEnd?: { latitude: number; longitude: number } | null
  isDrawMode: boolean
  drawingPoints: [number, number][]
  snappedGeometry?: GeoJSON.LineString | null
  currentHoverPoint?: [number, number] | null
  hoverPreviewGeometry?: GeoJSON.LineString | null
  isHoverSnapping?: boolean
  onDrawingPointAdd?: (coords: [number, number]) => void
  onDrawingHover?: (coords: [number, number] | null) => void
  isDeadEndDiscoveryMode?: boolean
  getAccessToken?: () => Promise<string | null>
  onForestRoadsError?: (message: string | null) => void
  tripRouteSchedule?: any[]
  itineraryRouteForWaypoint?: string | null
  selectedTripId?: string | null
  trip?: any | null
  isItineraryBuilder?: boolean
  showLibraryWaypoints?: boolean
  showPublicLands?: boolean
  onPublicLandsLoadingChange?: (isLoading: boolean) => void
  showDispersedCampsites?: boolean
  onDispersedCampsitesLoadingChange?: (isLoading: boolean) => void
  onDispersedCampsitesUpgradeRequired?: () => void
}) {
  // Filter out itinerary waypoints from regular waypoint layer
  // Itinerary waypoints have both trip_id and route_id and will be rendered as numbered badges
  const regularWaypoints = useMemo(() => {
    return waypoints.filter(wp => {
      // In itinerary builder mode, exclude all waypoints with trip_id (they're rendered as numbered markers)
      if (isItineraryBuilder) {
        return wp.trip_id == null
      }
      // Keep waypoints that are NOT itinerary waypoints
      // Itinerary waypoints have both trip_id and route_id
      const hasTripId = wp.trip_id != null
      const hasRouteId = (wp.route_id || (wp as any).routeId) != null
      return !(hasTripId && hasRouteId)
    })
  }, [waypoints, isItineraryBuilder])

  // Use all layer hooks
  useRouteLayer({ routes, selectedRoute })
  // Filter out GPX imported standalone waypoints from regular waypoint layer
  // They will be displayed with custom icons via useGpxImportedWaypointMarkers
  const regularWaypointsWithoutGpx = useMemo(() => {
    return regularWaypoints.filter(
      wp => !(wp.source === 'gpx_import' && !wp.trip_id)
    )
  }, [regularWaypoints])
  useWaypointLayer({ waypoints: regularWaypointsWithoutGpx, onWaypointClick, selectedWaypoint })
  useGpxImportedWaypointMarkers({ 
    waypoints, 
    selectedWaypoint, 
    onWaypointClick,
    showLibraryWaypoints,
    selectedTripId
  })
  // Legacy hooks for cleanup (will remove old circle layers if they exist)
  usePOILayer({ poiResults, selectedPOI })
  useDeadEndLayer({ deadEnds: deadEnds || [], selectedDeadEnd, onDeadEndClick })
  
  // New icon-based marker hooks
  usePOIMarkers({ poiResults, selectedPOI, onPOIClick })
  useCampsiteDiscoveryMarkers({ deadEnds: deadEnds || [], selectedDeadEnd, onDeadEndClick })
  useRouteBufferLayer({ routeGeometry: searchRouteGeometry, radiusMiles: searchRadiusMiles })
  useSearchBboxLayer({ visualization: searchBboxVisualization })
  useSearchResultMarker({ searchResult })
  useMapClickMarker({ mapClickCoordinates })
  useSelectedWaypointMarker({ selectedWaypoint, centerOnWaypoint })
  useMapCentering({ centerOnRoute, centerOnSearch, centerOnUserLocation, centerOnWaypoint, centerOnWaypoints, centerOnCandidate, centerOnPOI, centerOnDeadEnd })
  useDrawingLayers({
    isDrawMode,
    drawingPoints,
    snappedGeometry,
    currentHoverPoint,
    hoverPreviewGeometry,
    isHoverSnapping
  })
  useDrawingMode({ isDrawMode, onDrawingPointAdd, onDrawingHover })
  useTimelineRoutePreview({ snappedGeometry, isDrawMode })
  // Forest roads layer disabled – was slow and redundant with dead-end markers
  useForestRoadLayer({
    isEnabled: false,
    getAccessToken: getAccessToken || (async () => null),
    onError: onForestRoadsError
  })
  usePublicLandsLayer({
    isEnabled: showPublicLands || false,
    getAccessToken: getAccessToken || (async () => null),
    onLoadingChange: onPublicLandsLoadingChange
  })
  useDispersedCampsiteLayer({
    isEnabled: showDispersedCampsites || false,
    getAccessToken: getAccessToken || (async () => null),
    onLoadingChange: onDispersedCampsitesLoadingChange,
    onUpgradeRequired: onDispersedCampsitesUpgradeRequired
  })
  useRouteStartEndMarkers({ routes: tripRouteSchedule })
  
  // Render itinerary waypoints as numbered badges
  // For itinerary builder: use day-based markers that align with drag-and-drop panel
  // For regular itinerary view: use route-based markers
  // Always call both hooks - they check internally if they should render
  useItineraryWaypointMarkers({ 
    waypoints, 
    selectedRouteId: itineraryRouteForWaypoint || selectedRoute?.id || null,
    selectedTripId,
    // Disable regular markers when in itinerary builder mode
    disabled: isItineraryBuilder
  })
  
  // Use itinerary builder hook for day-based numbering
  // Always call the hook - it checks internally if it should render based on trip and selectedTripId
  useItineraryBuilderWaypointMarkers({
    waypoints,
    trip: isItineraryBuilder ? trip : null,
    selectedTripId: isItineraryBuilder ? selectedTripId : null,
    selectedWaypoint: isItineraryBuilder ? selectedWaypoint : null,
    onWaypointClick: isItineraryBuilder ? onWaypointClick : undefined
  })

  return null
}

function MapViewportListener({ onViewportChange }: { onViewportChange?: (viewport: MapViewport) => void }) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const callbackRef = useRef<typeof onViewportChange>()

  useEffect(() => {
    callbackRef.current = onViewportChange
  }, [onViewportChange])

  useEffect(() => {
    if (!map.current || !isLoaded || !callbackRef.current) {
      return
    }

    const emitViewport = () => {
      if (!map.current || !callbackRef.current) {
        return
      }

      const bounds = map.current.getBounds()
      if (!bounds) {
        return
      }
      const center = map.current.getCenter()
      callbackRef.current({
        zoom: map.current.getZoom(),
        bounds: {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest()
        },
        center: {
          latitude: center.lat,
          longitude: center.lng
        }
      })
    }

    const handleMoveEnd = () => emitViewport()
    const handleZoomEnd = () => emitViewport()
    const handleResize = () => emitViewport()
    const handleStyleData = () => emitViewport()

    if (styleLoaded) {
      emitViewport()
    } else {
      map.current.on('styledata', handleStyleData)
    }

    map.current.on('moveend', handleMoveEnd)
    map.current.on('zoomend', handleZoomEnd)
    map.current.on('resize', handleResize)

    return () => {
      if (!map.current) {
        return
      }
      map.current.off('moveend', handleMoveEnd)
      map.current.off('zoomend', handleZoomEnd)
      map.current.off('resize', handleResize)
      map.current.off('styledata', handleStyleData)
    }
  }, [map, isLoaded, styleLoaded])

  return null
}

/**
 * Component for managing map click popup
 */
function MapClickPopup({
  clickedMapPoint,
  onCloseMapClickPopup,
  onSaveMapClickPoint,
  popupRef,
}: {
  clickedMapPoint?: { latitude: number; longitude: number } | null
  onCloseMapClickPopup?: () => void
  onSaveMapClickPoint?: () => void
  popupRef: React.MutableRefObject<mapboxgl.Popup | null>
}) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()

  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) return

    if (!clickedMapPoint) {
      if (popupRef.current) {
        popupRef.current.remove()
        popupRef.current = null
      }
      return
    }

    const popupContent = document.createElement('div')
    popupContent.className = 'p-3 min-w-[200px]'
    const saveButtonLabel = 'Add to Library'
    const saveButtonClasses = 'w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors flex items-center justify-center gap-2'
    popupContent.innerHTML = `
      <div class="flex items-start justify-between gap-2 mb-2">
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900">Map Location</p>
          <p class="text-xs text-gray-500 font-mono mt-1">
            ${clickedMapPoint.latitude.toFixed(6)}, ${clickedMapPoint.longitude.toFixed(6)}
          </p>
        </div>
        <button 
          class="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
          data-action="close"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      <button 
        class="${saveButtonClasses}"
        data-action="save"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
        </svg>
        ${saveButtonLabel}
      </button>
    `

    const closeButton = popupContent.querySelector('[data-action="close"]')
    const saveButton = popupContent.querySelector('[data-action="save"]')
    
    closeButton?.addEventListener('click', () => {
      if (onCloseMapClickPopup) {
        onCloseMapClickPopup()
      }
    })
    
    saveButton?.addEventListener('click', () => {
      if (onSaveMapClickPoint) {
        onSaveMapClickPoint()
      }
    })

    if (popupRef.current) {
      popupRef.current.remove()
    }

    popupRef.current = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'bottom',
      offset: [0, -10]
    })
      .setLngLat([clickedMapPoint.longitude, clickedMapPoint.latitude])
      .setDOMContent(popupContent)
      .addTo(map.current)

    return () => {
      if (popupRef.current) {
        popupRef.current.remove()
        popupRef.current = null
      }
    }
  }, [map, isLoaded, styleLoaded, clickedMapPoint, onCloseMapClickPopup, onSaveMapClickPoint, popupRef])

  return null
}

// Memoize the component to prevent unnecessary re-renders
export const MemoizedMapboxMap = memo(MapboxMap, (prevProps, nextProps) => {
  return (
    prevProps.routes === nextProps.routes &&
    prevProps.waypoints === nextProps.waypoints &&
    prevProps.poiResults === nextProps.poiResults &&
    prevProps.searchResult === nextProps.searchResult &&
    prevProps.selectedRoute?.id === nextProps.selectedRoute?.id &&
    prevProps.selectedPOI?.id === nextProps.selectedPOI?.id &&
    prevProps.selectedWaypoint?.id === nextProps.selectedWaypoint?.id &&
    prevProps.isDrawMode === nextProps.isDrawMode &&
    prevProps.drawingPoints === nextProps.drawingPoints &&
    prevProps.snappedGeometry === nextProps.snappedGeometry &&
    prevProps.currentHoverPoint === nextProps.currentHoverPoint &&
    prevProps.hoverPreviewGeometry === nextProps.hoverPreviewGeometry &&
    prevProps.isSnapping === nextProps.isSnapping &&
    prevProps.isHoverSnapping === nextProps.isHoverSnapping &&
    prevProps.centerOnRoute?.id === nextProps.centerOnRoute?.id &&
    prevProps.centerOnWaypoint?.id === nextProps.centerOnWaypoint?.id &&
    prevProps.centerOnSearch === nextProps.centerOnSearch &&
    prevProps.clickedMapPoint === nextProps.clickedMapPoint &&
    prevProps.mapClickCoordinates === nextProps.mapClickCoordinates &&
    true
  )
})